import type { JSX } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { Alert } from "@gss/components/Alert";
import type { MeasurementStat } from "@gss/types/measurementStat";
import { Chart } from "../charts/Chart";
import type {
    FilterSerie,
    FilterSeriesDimensionScore,
    FilterSeriesStat,
} from "../filters/filters.types";
import { TooFewRespondents } from "../util/TooFewRespondents";
import { useTranslation } from "../util/Translation";
import { getScoreSeries } from "./getScoreSeries";

interface Props {
    stat: MeasurementStat;
    subtitle: string;
    filterSeries: FilterSerie[];
}

export function ResponseRate({
    stat,
    subtitle,
    filterSeries,
}: Props): JSX.Element {
    const [series, setSeries] = useState<FilterSeriesDimensionScore[]>();
    const [seriesNotOk, setSeriesNotOk] = useState<FilterSeriesStat[]>();
    const t = useTranslation().translation.stats;
    const responseRateText = t.responseRate;
    const invitationsText = t.mailings;

    const isBasedOnQuestion = useMemo(
        () => filterSeries.some((fs) => fs.basedOnQuestion),
        [filterSeries],
    );

    useEffect(() => {
        void getScoreSeries(stat.securityContext, filterSeries).then(
            ({ ok, notOk }) => {
                setSeries(ok);
                setSeriesNotOk(notOk);
            },
        );
    }, [filterSeries]);

    const renderBasedOnQuestionWarning = (title: string) => {
        if (!isBasedOnQuestion) {
            return null;
        }
        return (
            <Alert variant="warning" className="fst-italic">
                {title} {t.basedOnQuestionWarn}
            </Alert>
        );
    };

    const renderInvitations = () => {
        return (
            <Chart
                variant="dynamicColumn"
                title={invitationsText}
                subtitle={subtitle}
                filename={`${stat.measurement} ${invitationsText}`}
                yTitle={t.mailingsYAxis}
                categories={[t.mailingsNum, t.answeredNum]}
                series={series}
                integerTooltip
                yFormatter={(ctx) => {
                    if (typeof ctx.value === "number" && ctx.value >= 1000) {
                        return `${Math.round(ctx.value / 1000)}k`;
                    }
                    return ctx.value.toString();
                }}
                callback={(s) => {
                    const data = [
                        s.stat.invitees.invited,
                        s.stat.invitees.answered,
                    ];
                    return { data };
                }}
            />
        );
    };

    const renderResponseRate = () => {
        return (
            <Chart
                variant="column"
                title={responseRateText}
                subtitle={subtitle}
                filename={`${stat.measurement} ${responseRateText}`}
                yTitle={t.responseRateYAxis}
                series={series}
                suffix="%"
                callback={(s) => {
                    const data = [s.stat.invitees.responseRate];
                    return { data };
                }}
            />
        );
    };

    return (
        <>
            <TooFewRespondents series={seriesNotOk} />

            {renderBasedOnQuestionWarning(t.mailingsNum)}
            {renderInvitations()}

            <hr />

            {renderBasedOnQuestionWarning(responseRateText)}
            {renderResponseRate()}
        </>
    );
}
